import React from 'react';
import { normalizeToTranslationKey } from '@/utils';
import { Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { User, Phone, Zap } from 'lucide-react';
import { TableAction } from '@/components';
import { useResendStore } from '@/stores/useResendStore';
import { useListShippingRules } from '@/hooksApi/useShippingRule';
import { InventoryOrder } from '@/types/inventory';

type AvailableCarrier = InventoryOrder['available_carriers_detail'][number];

interface ResendSummaryRow {
  key: string;
  uid: string;
  platform_code: string;
  full_name: string;
  phone: string;
  selectedCarrier: AvailableCarrier | null;
  fee: number | null;
}

const ResendStep3: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedOrders,
    orderOverrides,
    carrierSelections,
    freshCarriers,
    ruleDispatchByOrder,
  } = useResendStore();
  // RS Ph3 — load rule list to render rule names in the summary banner.
  const hasAnyRule = Object.values(ruleDispatchByOrder).some((d) => d?.winningRuleUid);
  const { data: rulesListResp } = useListShippingRules(hasAnyRule);
  const dispatchedCount = Object.values(ruleDispatchByOrder).filter((d) => d?.winningRuleUid).length;

  const tableData: ResendSummaryRow[] = selectedOrders.map((order) => {
    const override = orderOverrides[order.uid];
    const activeSlug =
      carrierSelections[order.uid] ??
      override?.carrier_slug ??
      order.recommended_carrier_slug ??
      null;
    // Use fresh carriers (from re-fetch after region change) if available, otherwise fall back to original
    const carriers = freshCarriers[order.uid] ?? order.available_carriers_detail ?? [];
    const selectedCarrier =
      activeSlug != null
        ? carriers.find((c) => c.carrier_slug === activeSlug) ?? null
        : null;

    return {
      key: order.uid,
      uid: order.uid,
      platform_code: order.platform_code,
      full_name: override?.customer_data?.full_name ?? order.full_name,
      phone: override?.customer_data?.phone ?? order.phone,
      selectedCarrier,
      fee: selectedCarrier?.shipping_fee ?? null,
    };
  });

  const totalFee = tableData.reduce((acc, row) => acc + (row.fee ?? 0), 0);

  const columns: ColumnsType<ResendSummaryRow> = [
    {
      title: String(t('original_order')),
      dataIndex: 'platform_code',
      key: 'platform_code',
      render: (text: string) => (
        <span className='text-sm font-medium text-sd-foreground'>{text || '—'}</span>
      ),
    },
    {
      title: String(t('customer')),
      key: 'customer',
      render: (_: unknown, record: ResendSummaryRow) => (
        <Flex vertical gap={2}>
          <Flex align='center' gap={6}>
            <User size={14} className='text-gray-400 shrink-0' strokeWidth={1.5} />
            <span className='text-sm font-medium text-sd-foreground'>{record.full_name}</span>
          </Flex>
          <Flex align='center' gap={6}>
            <Phone size={12} className='text-sd-muted-foreground shrink-0' strokeWidth={1.5} />
            <span className='text-xs text-sd-muted-foreground'>{record.phone}</span>
          </Flex>
        </Flex>
      ),
    },
    {
      title: String(t('carrier')),
      key: 'carrier',
      render: (_: unknown, record: ResendSummaryRow) => {
        if (!record.selectedCarrier) {
          return (
            <span className='text-xs text-amber-500'>{String(t('select_carrier'))}</span>
          );
        }
        return (
          <span className='text-sm font-medium text-sd-foreground'>
            {String(t(normalizeToTranslationKey(record.selectedCarrier.carrier_name)))}
          </span>
        );
      },
    },
    {
      title: String(t('shipment_payment')),
      key: 'fee',
      render: (_: unknown, record: ResendSummaryRow) => (
        <span className='text-sm text-sd-foreground'>
          {record.fee != null && isFinite(record.fee)
            ? `${Math.round(record.fee).toLocaleString()} ${String(t('iqd'))}`
            : '—'}
        </span>
      ),
    },
  ];

  return (
    <div className='border border-solid border-sd-border rounded-2xl bg-white p-6'>
      <h2 className='text-base font-semibold text-sd-foreground mt-0 mb-1'>
        {String(t('resend_step3_title'))}
      </h2>
      <p className='text-sm text-sd-muted-foreground mt-0 mb-4'>
        {String(t('review_edit_before_finalizing'))}
      </p>

      {/* RS Ph3 — rule attribution summary banner (dispatched-only;
          shipping rules are binding, so there is no "overridden" count). */}
      {dispatchedCount > 0 && (
        <div className='mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3'>
          <Zap className='h-5 w-5 shrink-0 text-blue-600 mt-0.5' strokeWidth={2} />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-blue-900 m-0'>
              {String(
                t('resend_rule_dispatch_banner', {
                  count: dispatchedCount,
                  defaultValue:
                    '{{count}} order(s) auto-routed by your shipping rules.',
                }),
              )}
            </p>
            {rulesListResp?.object && rulesListResp.object.length > 0 && (
              <p className='text-xs text-blue-800/80 mt-0.5 m-0'>
                {String(t('rule_attribution_summary_hint', {
                  defaultValue: 'See order details after creation for the matched rule on each order.',
                }))}
              </p>
            )}
          </div>
        </div>
      )}

      <TableAction<ResendSummaryRow>
        columns={columns}
        data={tableData}
        pageSize={tableData.length || 10}
        hidePagination
        customClassName='import-table'
      />

      {tableData.length > 0 && (
        <div className='mt-4 flex justify-end'>
          <div className='rounded-xl border border-solid border-sd-border bg-sd-muted/30 px-5 py-3'>
            <span className='text-sm font-semibold text-sd-foreground'>
              {String(t('total'))}: {Math.round(totalFee).toLocaleString()} {String(t('iqd'))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResendStep3;
